import ArrowSwitcher from "@/components/portfolio/ArrowSwitcher";
import TransactionRow from "@/components/portfolio/TransactionRow";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { useUserActivity } from "@/hooks/useUserActivity";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";

type MonthlyTransactionsProps = {
  showControls?: boolean;
};

export const MonthlyTransactions = ({
  showControls = true,
}: MonthlyTransactionsProps) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      monthlyOverview: "Měsíční přehled",
      imported: "Importováno",
      importedFromStatement: "Importováno z bankovního výpisu",
    },
    en: {
      monthlyOverview: "Monthly overview",
      imported: "Imported",
      importedFromStatement: "Imported from a bank statement",
    },
  }[language];
  const {
    selectedEstate,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    monthTransactions,
  } = useEstateContext();
  const {
    activities,
    hasLoaded: hasLoadedActivity,
    hasError: hasActivityError,
  } = useUserActivity();
  const selectedPeriod = `${String(selectedMonth).padStart(2, "0")} ${selectedYear}`;
  const latestMonthImport = activities.find(
    (activity) =>
      activity.type === "BANK_STATEMENT_IMPORT" &&
      Number(activity.metadata.estateId) === selectedEstate?.id &&
      activity.metadata.periods?.includes(selectedPeriod),
  );
  const hasImportedTransactions = monthTransactions.some((transaction) =>
    Boolean(transaction.bankImportId),
  );
  const importLabel = latestMonthImport
    ? `${copy.imported} ${new Intl.DateTimeFormat(
        language === "cs" ? "cs-CZ" : "en-GB",
        { dateStyle: "medium" },
      ).format(
        new Date(latestMonthImport.createdAt),
      )} · ${latestMonthImport.metadata.format}`
    : hasImportedTransactions
      ? copy.importedFromStatement
      : null;

  return (
    <section className="border-t border-softiq-primary/10 dark:border-white/10">
      {showControls ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-2 px-5 pb-2 pt-3 sm:px-6">
            <p className="app-section-eyebrow">{copy.monthlyOverview}</p>
            {importLabel ? (
              <span className="app-pill-meta">
                <DocumentArrowUpIcon aria-hidden="true" className="size-3.5" />
                {importLabel}
              </span>
            ) : null}
          </div>
          <div className="flex items-start px-5 pb-3 sm:px-6">
            <ArrowSwitcher
              value={selectedMonth}
              min={0}
              max={13}
              onChange={(m) => {
                if (m < 1) {
                  setSelectedMonth(12);
                  setSelectedYear((y) => y - 1);
                } else if (m > 12) {
                  setSelectedMonth(1);
                  setSelectedYear((y) => y + 1);
                } else {
                  setSelectedMonth(m);
                }
              }}
              labelFormatter={(m) => (
                <span className="flex flex-col items-center leading-none">
                  <span>
                    {new Date(2000, m - 1).toLocaleString(
                      language === "cs" ? "cs-CZ" : "en-GB",
                      { month: "long" },
                    )}
                  </span>
                  <span className="mt-1 text-sm font-medium text-softiq-primary/45 dark:text-white/45">
                    {selectedYear}
                  </span>
                </span>
              )}
            />
          </div>
        </>
      ) : null}
      <div className="border-t border-softiq-primary/10 dark:border-white/10">
        <TransactionRow
          transactions={monthTransactions}
          activities={activities}
          hasLoadedActivity={hasLoadedActivity}
          hasActivityError={hasActivityError}
        />
      </div>
    </section>
  );
};
